import React, { useEffect, useState, useRef } from "react";
import { Container, Row, Col, Button, Spinner } from "reactstrap";
import { useNavigate } from "react-router-dom";
import styles from "../../../Library/AdverseEvent/css/adverseEvent.module.scss";
import ip from "../css/ipInventory.module.scss";
import { IP_PATHS } from "../../../../Routes/paths";
import {
  formatDate,
  GetUserData,
  perPageData,
  showToastMessage,
} from "../../../../common/service/CommonService";
import CommonTableIndexWise from "../../../../Components/Common/CommonTableIndexWise";
import CommonSearch from "../../../../Components/Common/CommonSearch";
import { useIpInventoryService } from "../api/useIpInventoryService";
import { queryClient } from "../../../../api/queryClient";
import DeleteModal from "../../../../Components/Common/DeleteModal";
import {
  PERMISSION_ACTIONS,
  PERMISSION_MODULES,
} from "../../../../common/constants/PermissionConstants";
import CommonButton from "../../../../Components/Common/CommonButton";
import PinVerificationModal from "../../DailyReconsilation/components/PinVerificationModal";
import StatCard from "../../../../Components/Common/StatCard";
import FilePreviewModal from "../../../Study/Attachments/components/FilePreviewModal";
import { useGlobalStudy } from "../../../../context/StudyContext";
import { useAuditService } from "modules/Audit/api/useAuditQuery";
import NoStudySelectedPlaceholder from "../../../../Components/Common/NoStudySelectedPlaceholder";
import { getIpInventoryColumns } from "../helpers/ipInventory.constant";
import { IpRecord } from "../helpers/ipInventory.types";

const IpInventoryList: React.FC = () => {
  document.title = "IP Inventory | ZENTIXS ORBIT";
  const navigate = useNavigate();
  const userData = GetUserData();
  const [searchText, setSearchText] = useState<string>("");
  const [currentPageNumber, setcurrentPageNumber] = useState<number>(1);
  const [deleteModal, setDeleteModal] = useState<boolean>(false);
  const [verifyModal, setVerifyModal] = useState<boolean>(false);
  const [selectedRecord, setSelectedRecord] = useState<IpRecord | null>(null);
  const [showDeleteReason, setShowDeleteReason] = useState<boolean>(false);

  //Audit
  const [isPreviewOpen, setIsPreviewOpen] = useState<boolean>(false);
  const [previewFile, setPreviewFile] = useState<File | null>(null);
  const { SelectAllMutation: AuditMutation } = useAuditService();
  const { selectedStudy: globalStudy } = useGlobalStudy();

  // Reset to page 1 whenever the global study filter changes
  const prevStudyRef = useRef(globalStudy?.studyid);
  useEffect(() => {
    if (prevStudyRef.current !== globalStudy?.studyid) {
      prevStudyRef.current = globalStudy?.studyid;
      setcurrentPageNumber(1);
    }
  }, [globalStudy?.studyid]);

  const onAuditClick = () => {
    const payload = {
      modulename: "IP Inventory",
      studyid: globalStudy?.studyid
    };

    AuditMutation.mutate(payload, {
      onSuccess: (data: any) => {
        if (data instanceof Blob) {
          const pdfFile = new File([data], "IP_Inventory_Audit_Log.pdf", {
            type: "application/pdf",
          });
          setPreviewFile(pdfFile);
          setIsPreviewOpen(true);
        }
      },
      onError: (error: any) => {
        showToastMessage("error", "Error fetching audit logs");
        console.error("Error fetching audit logs:", error);
      },
    });
  };

  const toggleDeleteModal = (): void => setDeleteModal(!deleteModal);

  const refreshTableData = () => {
    if (currentPageNumber === 1) {
      queryClient.invalidateQueries({
        queryKey: [
          "ipinventory-selectAll",
          currentPageNumber,
          perPageData,
          searchText,
        ],
      });
    } else {
      setcurrentPageNumber(1);
    }
  };

  const { SelectAllQuery, DeleteMutation, verifyPinMutation } =
    useIpInventoryService({
      searchText,
      pageNumber: currentPageNumber,
      pageSize: 5,
      studyid: Number(globalStudy?.studyid) ?? null,
      enableSelectall: true,
    });

  const {
    data: queryResponse,
    isLoading,
    isFetching,
    isError,
  } = SelectAllQuery as {
    data?: { data?: (IpRecord & { total_count: number })[] };
    isLoading: boolean;
    isFetching: boolean;
    isError: boolean;
  };

  const data: (IpRecord & { total_count: number })[] =
    queryResponse?.data ?? [];

  const [totalRecords, setTotalRecords] = useState<number>(0);

  useEffect(() => {
    if (data.length > 0) {
      setTotalRecords(data[0].total_count || 0);
    } else {
      setTotalRecords(0);
    }
  }, [data]);

  const onSearch = (value: string | null) => {
    setSearchText(value ?? "");
    setcurrentPageNumber(1);
  };

  const handleDeleteClick = (item: IpRecord) => {
    setSelectedRecord(item);
    setDeleteModal(true);
    console.log("selected record : ",selectedRecord);
  };

  const handleDeleteWithReason = (payload: {
    id: number | null;
    reason: string;
  }) => {
    if (!selectedRecord) return;

    DeleteMutation.mutate(
      { ipid: selectedRecord.ipid, receiptnumber: selectedRecord.receiptnumber, studyid: selectedRecord.studyid, updatereason: payload.reason },
      {
        onSuccess: (response) => {
          refreshTableData();
          showToastMessage("success", response.message);
          queryClient.invalidateQueries({
            queryKey: ["ipinventory-selectAll"],
          });
          queryClient.invalidateQueries({ queryKey: ["verification-notification-count"] });
          setSearchText("");
          setcurrentPageNumber(1);
          setShowDeleteReason(false);
          setSelectedRecord(null);
          toggleDeleteModal();
        },
        onError: (error: any) => {
          console.error("Delete Error:", error);
          showToastMessage("error", error?.message ?? error);
        },
      },
    );
  };

  const handleViewClick = (row: any) => {
    sessionStorage.setItem("receiptnumber", row.receiptnumber)
    navigate(IP_PATHS.VIEW.replace(":id", row.ipid.toString()));
  };

  const getRowActions = (row: any): any => {
    const isVerified = row?.isverified;
    const actions: any = {};

    if (!isVerified) {
      actions.onVerify = (r: any) => handleVerifyClick(r);
      actions.onEdit = (r: any) => {
        sessionStorage.setItem("receiptnumber", r.receiptnumber)
        navigate(IP_PATHS.EDIT.replace(":id", r.ipid.toString()));
      }
      actions.onDelete = (r: any) => handleDeleteClick(r);
    }
    return actions;
  };
  const handleVerifyClick = (item: IpRecord): void => {
    setSelectedRecord(item);
    setVerifyModal(true);
  };

  const handlePinVerify = (password: string) => {
    const payload = {
      verifypin: password,
      ipid: selectedRecord?.ipid,
      receiptnumber: selectedRecord?.receiptnumber,
      studyid: selectedRecord?.studyid,
      verifiedby: Number(userData?.userid),
    };

    verifyPinMutation.mutate(payload, {
      onSuccess: (response) => {
        queryClient.invalidateQueries({ queryKey: ["ipinventory-selectAll"] });
        queryClient.invalidateQueries({ queryKey: ["verification-notification-count"] });
        showToastMessage("success", response?.message);
        setVerifyModal(false);
        setSelectedRecord(null);
      },
      onError: (error: Error) => {
        showToastMessage("error", error?.message ?? error);
      },
    });
  };

  return (
    <div className={styles.dashboardContainer}>
      <Container fluid>
        {!globalStudy?.studyid ? (
          <div>
            <NoStudySelectedPlaceholder moduleName="inventory" />
          </div>
        ) : (
          <>
            <div className="d-flex justify-content-between align-items-center mt-4 mb-3 px-2">
              <div>
                <div className="d-flex align-items-center gap-2 mb-1">
                  <h5 className={`mb-0 ${styles.pageHeaderTitle}`}>Received IP</h5>
                </div>
                <p className={styles.pageHeaderSubtitle}>
                  List view of all received investigational products, shipment
                  checks, and storage disposition.
                </p>
              </div>
              <div className="d-flex gap-3 align-items-center">
                <CommonButton
                  module={PERMISSION_MODULES.IP_INVENTORY}
                  action={PERMISSION_ACTIONS.AUDIT}
                  color="primary"
                  size="sm"
                  onClick={onAuditClick}
                  disabled={AuditMutation.isPending}
                  outline
                  className="fw-600 "
                >
                  {AuditMutation.isPending ? (
                    <Spinner size="sm" className="me-2" />
                  ) : (
                    <i className="ri-history-line me-1"></i>
                  )}
                  Audit
                </CommonButton>
                <CommonButton
                  color="primary"
                  outline
                  className="px-2 fw-600"
                  size="sm"
                  module={PERMISSION_MODULES.IP_INVENTORY}
                  action={PERMISSION_ACTIONS.ADD}
                  onClick={() => navigate(IP_PATHS.ADD)}
                >
                  <i className="ri-add-line  me-1" /> Receive New IP
                </CommonButton>
              </div>
            </div>

            {/* ── Table & Stats Cards ───────────────────────────────────────── */}
            <Row className="g-3 mb-3 px-1">
              <Col md={4}>
                <StatCard
                  title="TOTAL RECORDS"
                  value={totalRecords}
                  subtext="Inventory received"
                  icon={<i className="ri-database-2-line" />}
                  bgColor="#eff6ff"
                  iconColor="#2563eb"
                  trendType="info"
                  subtextIcon="ri-stack-line"
                />
              </Col>

              <Col md={4}>
                <StatCard
                  title="PENDING VERIFICATION"
                  value={data[0]?.pending_verification || 0}
                  subtext="Needs verification"
                  icon={<i className="ri-time-line" />}
                  bgColor="#fffbeb"
                  iconColor="#d97706"
                  trendType="negative"
                  subtextIcon="ri-error-warning-line"
                />
              </Col>

              <Col md={4}>
                <StatCard
                  title="COMPLETED VERIFICATION"
                  value={data[0]?.completed_verification || 0}
                  subtext="Verification completed"
                  icon={<i className="ri-verified-badge-line" />}
                  bgColor="#ecfdf5"
                  iconColor="#059669"
                  trendType="success"
                  subtextIcon="ri-check-double-line"
                />
              </Col>
            </Row>
            <div className={styles.listPanel}>
              <div className={styles.panelHeader}>
                <h2>
                  <i className="ri-medicine-bottle-line me-2 text-primary fs-18" />
                  IP Receipt Records
                </h2>

                <CommonSearch onSearch={onSearch} searchText={searchText} />
              </div>

              <div className="px-4 pb-2">
                <CommonTableIndexWise
                  moduleName={PERMISSION_MODULES.IP_INVENTORY}
                  columns={getIpInventoryColumns(handleViewClick)}
                  data={data}
                  loading={isLoading || isFetching}
                  pageSize={5}
                  currentPage={currentPageNumber}
                  totalRecords={data[0]?.total_count || 0}
                  onPageChange={setcurrentPageNumber}
                  actions={getRowActions}
                />
              </div>

              {/* File Preview Modal */}
              {isPreviewOpen && (
                <FilePreviewModal
                  file={previewFile}
                  isOpen={isPreviewOpen}
                  size={"xl"}
                  onClose={() => {
                    setIsPreviewOpen(false);
                    setPreviewFile(null);
                  }}
                />
              )}

              {deleteModal && (
                <DeleteModal
                  isopen={deleteModal}
                  recordId={selectedRecord?.ipid}
                  recordName={selectedRecord?.receiptnumber}
                  toggle={() => setDeleteModal(!deleteModal)}
                  onDeleteClick={handleDeleteWithReason}
                  isLoading={DeleteMutation.isPending}
                  showDeleteReason={showDeleteReason}
                  toggleDeleteReason={() => setShowDeleteReason((p) => !p)}
                />
              )}

              <PinVerificationModal
                isOpen={verifyModal}
                toggle={() => setVerifyModal(false)}
                onVerify={handlePinVerify}
                isLoading={verifyPinMutation.isPending}
                title="Verify IP Receipt"
                description={
                  <>
                    Please enter your password to verify Receipt No: <strong>{selectedRecord?.receiptnumber}</strong>
                  </>
                }
                isInvalid={verifyPinMutation.isError}
              />
            </div>
          </>
        )}
      </Container>
    </div>
  );
};

export default IpInventoryList;
